import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { billingService } from './billingService';

export type DodoTxOutcome = 'success' | 'failed' | 'cancelled' | 'processing' | 'info';

export interface DodoPaymentTransactionRow {
  id: string;
  rowKey: string;
  createdAt: Date | null;
  source: string;
  eventType?: string | null;
  outcomeStatus: DodoTxOutcome | string;
  dodoPaymentId?: string | null;
  dodoSubscriptionId?: string | null;
  checkoutSessionId?: string | null;
  firebaseUid?: string | null;
  /** Resolved from `users` or Dodo payload */
  userEmail?: string | null;
  /** From Dodo payment / webhook when available */
  customerEmail?: string | null;
  paymentMethod?: string | null;
  discountCode?: string | null;
  amount?: number | null;
  /** Smallest currency unit when known (e.g. cents) */
  amountMinor?: number | null;
  currency?: string | null;
  dodoPaymentStatus?: string | null;
  failureCode?: string | null;
  failureMessage?: string | null;
  webhookDeliveryId?: string | null;
  httpStatus?: number | null;
  invoiceId?: string | null;
  plan?: string | null;
  billingPeriod?: string | null;
  amountUsd?: number | null;
  checkoutSource?: string | null;
}

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function mapDoc(id: string, data: Record<string, unknown>): DodoPaymentTransactionRow {
  const amountMinor =
    typeof data.amountMinor === 'number'
      ? data.amountMinor
      : typeof data.amount_minor === 'number'
        ? data.amount_minor
        : typeof data.total_amount === 'number'
          ? data.total_amount
          : typeof data.totalAmount === 'number'
            ? data.totalAmount
            : null;
  return {
    id,
    rowKey: id,
    createdAt: tsToDate(data.createdAt),
    source: String(data.source || 'unknown'),
    eventType: (data.eventType as string) || null,
    outcomeStatus: String(data.outcomeStatus || 'info'),
    dodoPaymentId: (data.dodoPaymentId as string) || null,
    dodoSubscriptionId: (data.dodoSubscriptionId as string) || null,
    checkoutSessionId: (data.checkoutSessionId as string) || null,
    firebaseUid: (data.firebaseUid as string) || null,
    customerEmail: (data.customerEmail as string) || (data.customer_email as string) || null,
    paymentMethod: (data.paymentMethod as string) || (data.payment_method as string) || null,
    discountCode: (data.discountCode as string) || (data.discount_code as string) || null,
    amount: typeof data.amount === 'number' ? data.amount : null,
    amountMinor,
    currency: (data.currency as string) || null,
    dodoPaymentStatus: (data.dodoPaymentStatus as string) || null,
    failureCode: (data.failureCode as string) || null,
    failureMessage: (data.failureMessage as string) || null,
    webhookDeliveryId: (data.webhookDeliveryId as string) || null,
    httpStatus: typeof data.httpStatus === 'number' ? data.httpStatus : null,
    invoiceId: (data.invoiceId as string) || null,
    plan: (data.plan as string) || null,
    billingPeriod: (data.billingPeriod as string) || null,
    amountUsd: typeof data.amountUsd === 'number' ? data.amountUsd : typeof data.amount_usd === 'number' ? data.amount_usd : null,
    checkoutSource: (data.checkoutSource as string) || null,
  };
}

/** Human-readable amount: prefers USD from `amountUsd`, then minor units / 100 for USD invoices. */
export function formatTransactionAmount(row: DodoPaymentTransactionRow): string {
  if (row.amountUsd != null && Number.isFinite(row.amountUsd) && row.amountUsd > 0) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.amountUsd);
  }
  const cur = (row.currency || 'usd').toUpperCase();
  const minor =
    row.amountMinor != null && typeof row.amountMinor === 'number'
      ? row.amountMinor
      : row.source === 'invoice_record' && typeof row.amount === 'number'
        ? row.amount
        : typeof row.amount === 'number'
          ? row.amount
          : null;
  if (minor == null) return '—';
  const dodoMinorSources = new Set([
    'dodo_webhook',
    'checkout_complete',
    'dodo_webhook_synced_payment',
    'dodo_webhook_handler',
  ]);
  if (cur === 'USD' && (row.source === 'invoice_record' || row.amountMinor != null || dodoMinorSources.has(row.source))) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(minor / 100);
  }
  if (cur === 'USD' && minor >= 50) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(minor / 100);
  }
  return `${minor} ${cur} (minor)`;
}

async function loadLedgerRows(ledgerLimit: number): Promise<DodoPaymentTransactionRow[]> {
  const rows: DodoPaymentTransactionRow[] = [];
  const tryOrdered = async () => {
    const q = query(
      collection(db, 'dodoPaymentTransactions'),
      orderBy('createdAt', 'desc'),
      limit(ledgerLimit)
    );
    const snap = await getDocs(q);
    snap.forEach((docSnap) => {
      rows.push(mapDoc(docSnap.id, docSnap.data() as Record<string, unknown>));
    });
  };
  const tryUnordered = async () => {
    const q = query(collection(db, 'dodoPaymentTransactions'), limit(ledgerLimit));
    const snap = await getDocs(q);
    snap.forEach((docSnap) => {
      rows.push(mapDoc(docSnap.id, docSnap.data() as Record<string, unknown>));
    });
  };

  try {
    await tryOrdered();
  } catch {
    rows.length = 0;
    try {
      await tryUnordered();
    } catch {
      // no access
    }
  }
  return rows;
}

async function loadWebhookProcessedRows(max: number): Promise<DodoPaymentTransactionRow[]> {
  const out: DodoPaymentTransactionRow[] = [];
  try {
    const snap = await getDocs(query(collection(db, 'dodoWebhookProcessedPayments'), limit(max)));
    snap.forEach((docSnap) => {
      const d = docSnap.data() as Record<string, unknown>;
      const paymentId = docSnap.id;
      out.push({
        id: `wproc:${paymentId}`,
        rowKey: `wproc:${paymentId}`,
        createdAt: tsToDate(d.recordedAt),
        source: 'dodo_webhook_synced_payment',
        eventType: 'payment.succeeded (subscription renew sync)',
        outcomeStatus: 'success',
        dodoPaymentId: paymentId,
        dodoSubscriptionId: (d.subscription_id as string) || null,
        firebaseUid: (d.uid as string) || null,
        customerEmail: (d.customerEmail as string) || (d.customer_email as string) || null,
        paymentMethod: (d.paymentMethod as string) || (d.payment_method as string) || null,
        discountCode:
          (d.discountCode as string) ||
          (d.discount_code as string) ||
          (d.discountId as string) ||
          (d.discount_id as string) ||
          null,
        checkoutSessionId: null,
        amount: null,
        amountMinor:
          typeof d.amountMinor === 'number'
            ? d.amountMinor
            : typeof d.amount_minor === 'number'
              ? d.amount_minor
              : null,
        currency: (d.currency as string) || 'usd',
        amountUsd: typeof d.amountUsd === 'number' ? d.amountUsd : typeof d.amount_usd === 'number' ? d.amount_usd : null,
        dodoPaymentStatus: null,
        failureMessage: null,
        webhookDeliveryId: null,
      });
    });
  } catch {
    // rules
  }
  return out;
}

async function loadWebhookDeliveryRows(max: number): Promise<DodoPaymentTransactionRow[]> {
  const out: DodoPaymentTransactionRow[] = [];
  try {
    const snap = await getDocs(query(collection(db, 'dodoWebhookDeliveries'), limit(max)));
    snap.forEach((docSnap) => {
      const d = docSnap.data() as Record<string, unknown>;
      const whId = (d.webhookId as string) || docSnap.id;
      const type = String(d.type || 'unknown');
      const proc = String(d.status || '');
      let outcome: string = 'info';
      if (proc === 'completed') outcome = type.startsWith('payment.') ? 'success' : 'info';
      else if (proc === 'processing') outcome = 'processing';
      const dodoSubscriptionId =
        (d.context_subscription_id as string) ||
        (d.contextSubscriptionId as string) ||
        (d.subscription_id as string) ||
        null;
      const dodoPaymentId =
        (d.context_payment_id as string) ||
        (d.contextPaymentId as string) ||
        (d.payment_id as string) ||
        null;
      const firebaseUid =
        (d.context_firebase_uid as string) ||
        (d.contextFirebaseUid as string) ||
        (d.firebase_uid as string) ||
        null;
      out.push({
        id: `delivery:${docSnap.id}`,
        rowKey: `delivery:${docSnap.id}`,
        createdAt: tsToDate(d.receivedAt) || tsToDate(d.processedAt),
        source: 'dodo_webhook_delivery',
        eventType: type,
        outcomeStatus: outcome,
        dodoPaymentId,
        dodoSubscriptionId,
        firebaseUid,
        checkoutSessionId: null,
        amount: null,
        currency: null,
        dodoPaymentStatus: proc || null,
        failureMessage: null,
        webhookDeliveryId: whId,
      });
    });
  } catch {
    // rules
  }
  return out;
}

async function resolveFirebaseUidFromDodoSubscriptions(
  subscriptionIds: string[]
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const uniq = Array.from(new Set(subscriptionIds.filter(Boolean))).slice(0, 80);
  for (let i = 0; i < uniq.length; i += 10) {
    const batch = uniq.slice(i, i + 10);
    const snaps = await Promise.all(batch.map((sid) => getDoc(doc(db, 'dodoSubscriptionIndex', sid))));
    snaps.forEach((s, j) => {
      if (s.exists()) {
        const uid = (s.data() as { uid?: string })?.uid;
        if (uid) out.set(batch[j], uid);
      }
    });
  }
  return out;
}

type DodoPaymentFieldsPayload = {
  paymentMethod?: string | null;
  discountCode?: string | null;
  customerEmail?: string | null;
  amountMinor?: number | null;
  amountUsd?: number | null;
  currency?: string | null;
};

/** Live Dodo API read (admin callable) when Firestore rows predate enrichment. */
async function enrichRowsWithLiveDodoPaymentLookup(rows: DodoPaymentTransactionRow[]): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  const need = new Set<string>();
  for (const r of rows) {
    if (!r.dodoPaymentId) continue;
    const missingAmount =
      r.source !== 'invoice_record' && r.amountMinor == null && r.amountUsd == null && (r.amount == null || r.amount === 0);
    const missingMethod = !r.paymentMethod;
    if (missingAmount || missingMethod) need.add(r.dodoPaymentId);
    if (need.size >= 40) break;
  }
  if (need.size === 0) return;
  const fn = httpsCallable(functions, 'adminGetDodoPaymentDetails');
  const ids = Array.from(need);
  for (let i = 0; i < ids.length; i += 5) {
    const chunk = ids.slice(i, i + 5);
    await Promise.all(
      chunk.map(async (paymentId) => {
        try {
          const res = await fn({ paymentId });
          const payload = res.data as { ok?: boolean; fields?: DodoPaymentFieldsPayload };
          const f = payload?.fields;
          if (!f) return;
          for (const r of rows) {
            if (r.dodoPaymentId !== paymentId) continue;
            if (r.amountMinor == null && f.amountMinor != null) r.amountMinor = f.amountMinor;
            if (r.amountUsd == null && f.amountUsd != null) r.amountUsd = f.amountUsd;
            if (!r.paymentMethod && f.paymentMethod) r.paymentMethod = f.paymentMethod;
            if (!r.discountCode && f.discountCode) r.discountCode = f.discountCode;
            if (!r.customerEmail && f.customerEmail) r.customerEmail = f.customerEmail;
            if (!r.currency && f.currency) r.currency = f.currency;
          }
        } catch (e) {
          console.warn('[adminGetDodoPaymentDetails]', paymentId, e);
        }
      })
    );
  }
}

async function resolveUserEmails(uids: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const unique = Array.from(new Set(uids.filter(Boolean)));
  for (let i = 0; i < unique.length; i += 10) {
    const batch = unique.slice(i, i + 10);
    const snaps = await Promise.all(batch.map((uid) => getDoc(doc(db, 'users', uid))));
    snaps.forEach((s, j) => {
      const uid = batch[j];
      if (s.exists()) {
        const d = s.data() as Record<string, unknown>;
        const email =
          (typeof d.email === 'string' && d.email) ||
          (typeof d.userEmail === 'string' && d.userEmail) ||
          (typeof d.user_email === 'string' && d.user_email) ||
          null;
        if (email) map.set(uid, email);
      }
    });
  }
  return map;
}

function enrichRows(rows: DodoPaymentTransactionRow[]): DodoPaymentTransactionRow[] {
  const payToInvoiceAmount = new Map<string, { amount: number; currency: string }>();
  for (const r of rows) {
    if (r.source === 'invoice_record' && r.dodoPaymentId && typeof r.amount === 'number') {
      payToInvoiceAmount.set(r.dodoPaymentId, {
        amount: r.amount,
        currency: r.currency || 'usd',
      });
    }
  }
  for (const r of rows) {
    if ((!r.amount && r.amount !== 0) && r.amountMinor == null && r.amountUsd == null && r.dodoPaymentId) {
      const inv = payToInvoiceAmount.get(r.dodoPaymentId);
      if (inv) {
        r.amount = inv.amount;
        r.currency = inv.currency;
      }
    }
  }
  return rows;
}

/** Copy amount / method / discount from ledger rows onto wproc / delivery rows with the same payment id. */
function mergePaymentDetailsFromLedger(rows: DodoPaymentTransactionRow[]): void {
  const byPayment = new Map<string, DodoPaymentTransactionRow>();
  for (const r of rows) {
    if (!r.dodoPaymentId) continue;
    if (r.source !== 'dodo_webhook' && r.source !== 'checkout_complete') continue;
    const prev = byPayment.get(r.dodoPaymentId);
    const rTime = r.createdAt?.getTime() ?? 0;
    const pTime = prev?.createdAt?.getTime() ?? 0;
    if (!prev || rTime >= pTime) byPayment.set(r.dodoPaymentId, r);
  }
  for (const r of rows) {
    if (!r.dodoPaymentId) continue;
    const src = byPayment.get(r.dodoPaymentId);
    if (!src || src.rowKey === r.rowKey) continue;
    if (r.amountMinor == null && src.amountMinor != null) r.amountMinor = src.amountMinor;
    if (r.amountUsd == null && src.amountUsd != null) r.amountUsd = src.amountUsd;
    if (!r.paymentMethod && src.paymentMethod) r.paymentMethod = src.paymentMethod;
    if (!r.discountCode && src.discountCode) r.discountCode = src.discountCode;
    if (!r.currency && src.currency) r.currency = src.currency;
    if (!r.customerEmail && src.customerEmail) r.customerEmail = src.customerEmail;
  }
}

/**
 * Loads Dodo-related payment activity and enriches with user emails + invoice amounts.
 */
export async function fetchDodoAdminTransactions(options?: {
  ledgerLimit?: number;
  invoiceLimit?: number;
}): Promise<DodoPaymentTransactionRow[]> {
  const ledgerLimit = options?.ledgerLimit ?? 500;
  const invoiceLimit = options?.invoiceLimit ?? 200;

  const rows: DodoPaymentTransactionRow[] = [];
  const seenPaymentIds = new Set<string>();
  const seenWebhookDeliveryIds = new Set<string>();

  const ledger = await loadLedgerRows(ledgerLimit);
  const ledgerHasEnrichedPayment = (paymentId: string) =>
    ledger.some(
      (r) =>
        r.dodoPaymentId === paymentId &&
        (r.amountMinor != null || r.amountUsd != null || !!r.paymentMethod)
    );

  for (const r of ledger) {
    rows.push(r);
    if (r.dodoPaymentId) seenPaymentIds.add(r.dodoPaymentId);
    if (r.webhookDeliveryId) seenWebhookDeliveryIds.add(r.webhookDeliveryId);
  }

  const wproc = await loadWebhookProcessedRows(300);
  for (const r of wproc) {
    if (r.dodoPaymentId && ledgerHasEnrichedPayment(r.dodoPaymentId)) continue;
    if (r.dodoPaymentId) seenPaymentIds.add(r.dodoPaymentId);
    rows.push(r);
  }

  const deliveries = await loadWebhookDeliveryRows(400);
  for (const r of deliveries) {
    const wh = r.webhookDeliveryId;
    if (wh && seenWebhookDeliveryIds.has(wh)) continue;
    if (wh) seenWebhookDeliveryIds.add(wh);
    rows.push(r);
  }

  try {
    const invoices = await billingService.getAllInvoices(invoiceLimit);
    for (const inv of invoices) {
      const pid = inv.paymentId;
      if (!pid || seenPaymentIds.has(pid)) continue;
      seenPaymentIds.add(pid);
      const created = inv.created ? new Date(inv.created * 1000) : null;
      rows.push({
        id: `invoice:${inv.id}`,
        rowKey: `invoice:${inv.id}`,
        createdAt: created,
        source: 'invoice_record',
        eventType: null,
        outcomeStatus: inv.status === 'paid' ? 'success' : 'info',
        dodoPaymentId: pid,
        dodoSubscriptionId: null,
        checkoutSessionId: null,
        firebaseUid: inv.userId || (typeof inv.customer === 'string' ? inv.customer : null),
        amount: typeof inv.amount_paid === 'number' ? inv.amount_paid : null,
        currency: inv.currency || 'usd',
        dodoPaymentStatus: inv.status || null,
        failureMessage: null,
        invoiceId: inv.id,
        plan: inv.plan || null,
        billingPeriod: inv.billingPeriod || null,
        amountUsd: null,
        checkoutSource: 'firestore_invoice',
      });
    }
  } catch {
    // optional
  }

  enrichRows(rows);
  mergePaymentDetailsFromLedger(rows);
  await enrichRowsWithLiveDodoPaymentLookup(rows);

  const subIds = rows.map((r) => r.dodoSubscriptionId).filter((x): x is string => !!x);
  const subToUid = await resolveFirebaseUidFromDodoSubscriptions(subIds);
  for (const r of rows) {
    if (!r.firebaseUid && r.dodoSubscriptionId && subToUid.has(r.dodoSubscriptionId)) {
      r.firebaseUid = subToUid.get(r.dodoSubscriptionId) || null;
    }
  }

  const uids = rows.map((r) => r.firebaseUid).filter((x): x is string => !!x);
  const emailMap = await resolveUserEmails(uids);
  for (const r of rows) {
    if (r.firebaseUid && emailMap.has(r.firebaseUid)) {
      r.userEmail = emailMap.get(r.firebaseUid) || null;
    }
    if (!r.userEmail && r.customerEmail) {
      r.userEmail = r.customerEmail;
    }
  }

  rows.sort((a, b) => {
    const ta = a.createdAt?.getTime() ?? 0;
    const tb = b.createdAt?.getTime() ?? 0;
    return tb - ta;
  });

  return rows;
}
